import { MenuItem, TextField } from "@mui/material";
import { GridFilterItem, useGridApiContext } from "@mui/x-data-grid-pro";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { DateTimePicker as _DateTimePicker } from "@flow-frontend-npm/date-pickers";
import moment, { Moment } from "moment";
import * as React from "react";
import { getDateFormat, getDateTimeFormat } from "utils/dateTime";

type FlowDateTimePickerProps = React.ComponentProps<typeof _DateTimePicker> & {
    format?: string;
    maxDateTime?: Moment;
};
const FlowDateTimePicker =
    _DateTimePicker as React.ComponentType<FlowDateTimePickerProps>;

// Turn a moment format into a readable placeholder, e.g.
// "DD.MM.YYYY,  HH:mm [Uhr]" -> "DD.MM.YYYY, HH:mm Uhr"
const toPlaceholder = (format: string) =>
    format.replace(/[[\]]/g, "").replace(/\s+/g, " ").trim();

function useGridLocaleText() {
    const apiRef = useGridApiContext();
    return React.useCallback(
        (key: string) =>
            String((apiRef.current as any).getLocaleText(key as any)),
        [apiRef]
    );
}

export function NoInput() {
    const getLocaleTextString = useGridLocaleText();

    return (
        <TextField
            size="small"
            variant="outlined"
            label={getLocaleTextString("filterPanelInputLabel")}
            fullWidth
            disabled
            sx={{ visibility: "hidden" }}
        />
    );
}

export function TextInput({
    item,
    onChange,
}: {
    item: GridFilterItem;
    onChange: (value: unknown) => void;
}) {
    const getLocaleTextString = useGridLocaleText();

    return (
        <TextField
            size="small"
            variant="outlined"
            label={getLocaleTextString("filterPanelInputLabel")}
            value={item.value ?? ""}
            onChange={(e) => onChange(e.target.value)}
            fullWidth
        />
    );
}

export function NumberInput({
    item,
    onChange,
}: {
    item: GridFilterItem;
    onChange: (value: unknown) => void;
}) {
    const getLocaleTextString = useGridLocaleText();

    return (
        <TextField
            size="small"
            type="number"
            variant="outlined"
            label={getLocaleTextString("filterPanelInputLabel")}
            value={item.value ?? ""}
            onChange={(e) => onChange(e.target.value)}
            fullWidth
        />
    );
}

export function DateInput({
    item,
    onChange,
}: {
    item: GridFilterItem;
    onChange: (value: unknown) => void;
}) {
    const getLocaleTextString = useGridLocaleText();

    return (
        <LocalizationProvider dateAdapter={AdapterMoment}>
            <DatePicker
                format={getDateFormat()}
                value={item.value ? moment(item.value) : null}
                onChange={(value: Moment | null) =>
                    onChange(
                        value && value.isValid()
                            ? value.format("YYYY-MM-DD")
                            : undefined
                    )
                }
                maxDate={moment("9999-12-31")}
                slotProps={{
                    textField: {
                        size: "small",
                        variant: "outlined",
                        fullWidth: true,
                        label: getLocaleTextString("filterPanelInputLabel"),
                        placeholder: toPlaceholder(getDateFormat()),
                        InputLabelProps: { shrink: true },
                    },
                }}
            />
        </LocalizationProvider>
    );
}

export function DateTimeInput({
    item,
    onChange,
}: {
    item: GridFilterItem;
    onChange: (value: unknown) => void;
}) {
    const getLocaleTextString = useGridLocaleText();

    return (
        <FlowDateTimePicker
            id="custom-filter-datetime-input"
            label={getLocaleTextString("filterPanelInputLabel")}
            alwaysShowTimePicker
            format={getDateTimeFormat()}
            value={(item.value ? moment(item.value) : null) as unknown as object}
            onChange={(value) => {
                const nextValue = value as Moment | null;
                onChange(
                    nextValue && nextValue.isValid()
                        ? nextValue.toDate()
                        : undefined
                );
            }}
            inputProps={{
                size: "small",
                placeholder: toPlaceholder(getDateTimeFormat()),
                InputLabelProps: { shrink: true },
            }}
            maxDateTime={moment("9999-12-31T23:59")}
        />
    );
}

export function BooleanInput({
    item,
    valueOptions,
    onChange,
}: {
    item: GridFilterItem;
    valueOptions?: Array<{ value: string; label: string }>;
    onChange: (value: unknown) => void;
}) {
    const getLocaleTextString = useGridLocaleText();

    const selectedValue =
        item.value === true || item.value === "true" ? true : false;
    return (
        <TextField
            size="small"
            select
            variant="outlined"
            label={getLocaleTextString("filterPanelInputLabel")}
            fullWidth
            value={selectedValue}
            onChange={(e) => onChange(e.target.value === "true")}
        >
            {(
                valueOptions || [
                    {
                        value: true,
                        label: getLocaleTextString("booleanCellTrueLabel"),
                    },
                    {
                        value: false,
                        label: getLocaleTextString("booleanCellFalseLabel"),
                    },
                ]
            ).map((o) => (
                <MenuItem key={o.value} value={o.value}>
                    {o.label}
                </MenuItem>
            ))}
        </TextField>
    );
}

export function MultipleInput({
    item,
    valueOptions,
    onChange,
}: {
    item: GridFilterItem;
    valueOptions: Array<{ value: string; label: string }>;
    onChange: (value: unknown) => void;
}) {
    const getLocaleTextString = useGridLocaleText();

    const selected: string[] = Array.isArray(item.value)
        ? (item.value as string[])
        : item.value
            ? [String(item.value)]
            : [];

    return (
        <TextField
            size="small"
            select
            variant="outlined"
            label={getLocaleTextString("filterPanelInputLabel")}
            fullWidth
            SelectProps={{ multiple: true }}
            value={selected}
            onChange={(e: React.ChangeEvent<{ value: unknown }>) => {
                const next = Array.isArray(e.target.value)
                    ? e.target.value
                    : [];
                onChange(next);
            }}
        >
            {valueOptions.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                    {o.label}
                </MenuItem>
            ))}
        </TextField>
    );
}

export function SingleSelectInput({
    item,
    valueOptions,
    onChange,
}: {
    item: GridFilterItem;
    valueOptions: Array<{ value: string; label: string }>;
    onChange: (value: unknown) => void;
}) {
    const getLocaleTextString = useGridLocaleText();

    const selected =
        item.value === undefined || item.value === null
            ? ""
            : String(item.value);
    return (
        <TextField
            size="small"
            select
            variant="outlined"
            label={getLocaleTextString("filterPanelInputLabel")}
            fullWidth
            value={selected}
            onChange={(e: React.ChangeEvent<{ value: unknown }>) => {
                const v = e.target.value;
                if (v === "true") {
                    onChange(true);
                } else if (v === "false") {
                    onChange(false);
                } else {
                    onChange(v);
                }
            }}
        >
            {valueOptions.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                    {o.label}
                </MenuItem>
            ))}
        </TextField>
    );
}
